import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/auth/AuthContext";
import SystemUpdateViewer from "./SystemUpdateViewer";
import { preloadStoryItems } from "./preloadStories";
import { resolveBackground } from "./storyBackgrounds";

function StoryRing({ items, size = 56 }) {
  const n = Math.max(items.length, 1);
  const cx = size / 2;
  const r = size / 2 - 2.5;
  const circumference = 2 * Math.PI * r;
  const gap = n <= 1 ? 0 : Math.min(12, circumference * 0.1);
  const segment = circumference / n - gap;

  return (
    <svg className="absolute inset-0" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {items.map((item, i) => (
        <circle
          key={item.id}
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={item.viewed ? "#94a3b8" : "#34d399"}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={`${segment} ${circumference - segment}`}
          transform={`rotate(${(360 / n) * i - 90} ${cx} ${cx})`}
        />
      ))}
    </svg>
  );
}

function StoryPreview({ update }) {
  const mediaType = update?.media_type || "none";
  const text = String(update?.text || update?.text_blocks?.[0]?.text || "").trim();

  if (mediaType === "image" && update.media_url) {
    return <img src={update.media_url} alt="" className="h-full w-full object-cover" />;
  }
  if (mediaType === "video" && update.media_url) {
    return (
      <video
        src={update.media_url}
        muted
        playsInline
        preload="metadata"
        className="h-full w-full object-cover"
      />
    );
  }
  if (text) {
    return (
      <div className="flex h-full w-full items-center justify-center px-1" style={resolveBackground(update)}>
        <span className="line-clamp-3 text-center text-[8px] font-bold leading-tight text-white">
          {text}
        </span>
      </div>
    );
  }
  return (
    <div className="flex h-full w-full items-center justify-center bg-emerald-600 text-white">
      <Sparkles className="h-5 w-5" />
    </div>
  );
}

export default function SystemUpdateStories({ className = "", fallback = null, size = 56 }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [session, setSession] = useState(null);

  const { data } = useQuery({
    queryKey: ["system-updates-feed"],
    queryFn: () => base44.entities.SystemUpdate.feed(),
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: session ? false : 60_000,
  });

  const items = data?.items || [];
  const unseen = data?.unseen || 0;
  const preview = useMemo(
    () => items.find((i) => !i.viewed) || items[0],
    [items]
  );

  useEffect(() => {
    if (!items.length) return;
    preloadStoryItems(items);
  }, [items]);

  if (!items.length && !session) return fallback;

  return (
    <div className={className}>
      {items.length ? (
        <button
          type="button"
          onClick={async () => {
            const start = items.findIndex((i) => !i.viewed);
            const startIndex = start < 0 ? 0 : start;
            const snapshot = items.map((item) => ({ ...item }));
            await preloadStoryItems(snapshot.slice(startIndex, startIndex + 2));
            setSession({ items: snapshot, startIndex });
          }}
          aria-label={unseen > 0 ? "עדכונים חדשים" : "עדכוני מערכת"}
          className="relative shrink-0"
        >
          <div className="relative" style={{ width: size, height: size }}>
            <StoryRing items={items} size={size} />
            <div className="absolute inset-[4px] overflow-hidden rounded-full bg-emerald-900">
              <StoryPreview update={preview} />
            </div>
          </div>
          {unseen > 0 ? (
            <span className="absolute -left-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unseen}
            </span>
          ) : null}
        </button>
      ) : fallback}

      {session ? (
        <SystemUpdateViewer
          items={session.items}
          startIndex={session.startIndex}
          onClose={() => {
            setSession(null);
            queryClient.invalidateQueries({ queryKey: ["system-updates-feed"] });
          }}
          onViewed={async (id) => {
            try {
              await base44.entities.SystemUpdate.markViewed(id);
            } catch {
              /* נשאר מקומי עד סגירה */
            }
          }}
        />
      ) : null}
    </div>
  );
}
